import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';

import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Select, { SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import {
  ProviderTypesMap,
  ProviderTypeScrollPosition,
} from '@apps/shared/src/providerSearch/types/providerSearch';
import { getAxiosRequestConfig } from '@apps/shared/src/utilities/http';
import {
  setProviderType,
  setProviderSpecialty,
  setScrollPosition,
  setProviderTypesMap,
} from '@apps/shared/src/providerSearch/actions';
import { RootState } from '../../store';
import { IncompleteTheme } from '../../shared/types/theme';

const Header = styled(Typography)(({ theme }) => ({
  ...(theme as IncompleteTheme).sidebar.headerText,
}));

const Header2 = styled(Typography)(({ theme }) => ({
  ...(theme as IncompleteTheme).sidebar.headerText2,
}));

const Title = styled(Typography)({
  marginBottom: '0.25rem',
});

const StyledSelect = styled(Select)({
  width: '100%',
  '& .MuiSelect-select': {
    background: '#ffffff',
    border: '1px solid #E5EBF5',
    padding: '12px 16px',
  },
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...(theme as IncompleteTheme).selectMenuItem,
  display: 'block',
  fontWeight: 'bold',
  padding: '12px 16px',
  minHeight: '48px',
}));

const MenuItemText = styled('span')({
  display: 'block',
});

const MenuItemSubText = styled('span')({
  display: 'block',
  fontSize: '12px',
  fontWeight: 'lighter',
  paddingLeft: '0.5rem',
});

const defaultProviderCategory = '';
const defaultProviderTitle = 'All Specialties';

function getProviderCountText(numProviders: number): string {
  return `${numProviders} ${numProviders === 1 ? 'Provider' : 'Providers'}`;
}

function findProviderCategoryFromTitle(
  providerTypesMap: ProviderTypesMap,
  keyTitle: string
): string {
  for (const category in providerTypesMap) {
    if (providerTypesMap[category].includes(keyTitle)) return category;
  }
  return '';
}

const mapStateToProps = ({ providerSearch, planSelect }: RootState) => ({
  providerTypesMap: providerSearch.providerTypesMap,
  scrollPosition: providerSearch.scrollPosition,
  selectedProviderType: providerSearch.selectedProviderType,
  selectedProviderSpecialty: providerSearch.selectedProviderSpecialty,
  isLoading: providerSearch.isLoadingProviderList,
  radius: providerSearch.radius,
  zip: providerSearch.selectedZipCode,
  searchInput: providerSearch.searchInput,
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  providergendercode: providerSearch.providergendercode,
  entityType: providerSearch.entitytype,
  selectedPlan: planSelect.selectedPlan?.id,
});

const mapDispatchToProps = {
  setProviderType,
  setProviderSpecialty,
  setScrollPosition,
  setProviderTypesMap,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type Props = StateProps & typeof mapDispatchToProps;

export function ProviderTypeFilter({
  setProviderType,
  setProviderSpecialty,
  setScrollPosition,
  setProviderTypesMap,
  selectedAccessibilities,
  providerTypesMap,
  providergendercode,
  entityType,
  selectedPlan,
  scrollPosition,
  selectedProviderType,
  selectedProviderSpecialty,
  isLoading,
  radius,
  zip,
  searchInput,
}: Props): JSX.Element {
  const [providerCategory, setProviderCategory] = useState(selectedProviderType);
  const [providerTitle, setProviderTitle] = useState(defaultProviderTitle);
  const [providerApiData, setProviderApiData] = useState<any>(null);
  const manuallyUpdatedRef = useRef(false);

  // Fetch taxonomy mapping API and parse counts
  useEffect(() => {
    const fetchProviderTaxonomyMapping = async () => {
      try {
        const baseUrl = '/api/provider-search/providerTaxonomy-mapping';
        const params = new URLSearchParams({
          radius: String(radius),
          zip: zip || '',
          text: searchInput || '',
          providergendercode: providergendercode || '',
          entityType: entityType || '',
          selectedPlan: selectedPlan || '',
        });
        if (selectedAccessibilities.length > 0) {
          params.set('accessibilities', selectedAccessibilities.join(','));
        }
        const uploadUrl = `${baseUrl}?${params.toString()}`;
        const response = await axios.get(uploadUrl, getAxiosRequestConfig());

        const data = response.data.providerType;
        setProviderApiData(data);

        // Build providerTypesMap from API response
        const typesMap: ProviderTypesMap = {};
        for (const category in data) {
          if (Object.prototype.hasOwnProperty.call(data, category)) {
            typesMap[category] = Object.keys(data[category].Speciality || {});
          }
        }

        setProviderTypesMap(typesMap);
      } catch (error) {
        console.error('Failed to fetch taxonomy mapping:', error);
      }
    };
    if (zip.length === 5) {
      fetchProviderTaxonomyMapping();
    }
  }, [
    radius,
    zip,
    searchInput,
    setProviderTypesMap,
    selectedAccessibilities,
    providergendercode,
    entityType,
    selectedPlan,
  ]);

  // Reset state based on selection
  useEffect(() => {
    if (manuallyUpdatedRef.current) {
      manuallyUpdatedRef.current = false;
      return;
    }

    const hasType = selectedProviderType && selectedProviderType !== '';
    const hasSpecialty = selectedProviderSpecialty && selectedProviderSpecialty !== '';

    if (!hasType && !hasSpecialty) {
      setProviderCategory(defaultProviderCategory);
      setProviderTitle(defaultProviderTitle);
    } else if (hasType && hasSpecialty) {
      // Both are set correctly from Redux
      setProviderCategory(selectedProviderType);
      setProviderTitle(selectedProviderSpecialty);
    } else if (hasSpecialty && !hasType) {
      // Only specialty is set (infer type from it)
      const inferredType = findProviderCategoryFromTitle(
        providerTypesMap,
        selectedProviderSpecialty
      );
      setProviderCategory(inferredType || defaultProviderCategory);
      setProviderTitle(selectedProviderSpecialty);
    } else if (hasType && !hasSpecialty) {
      // Only type is set (no title)
      setProviderCategory(selectedProviderType);
      setProviderTitle(defaultProviderTitle);
    }
  }, [selectedProviderType, selectedProviderSpecialty, providerTypesMap]);

  // ✅ Derive counts directly from `providerApiData`
  const counts = useMemo(() => {
    const counts = {
      categories: {} as Record<string, number>,
      titles: {} as Record<string, number>,
    };
    if (!providerApiData) return counts;

    for (const category in providerApiData) {
      if (Object.prototype.hasOwnProperty.call(providerApiData, category)) {
        const data = providerApiData[category];
        counts.categories[category] = data.providers || 0;

        for (const title in data.Speciality || {}) {
          if (Object.prototype.hasOwnProperty.call(data.Speciality, title)) {
            counts.titles[title] = data.Speciality[title];
          }
        }
      }
    }

    return counts;
  }, [providerApiData]);

  const categories = useMemo(
    () => Object.keys(counts.categories).filter(c => counts.categories[c] > 0),
    [counts]
  );

  const titles = useMemo(
    () => providerTypesMap[providerCategory]?.filter(t => counts.titles[t] > 0),
    [providerTypesMap, providerCategory, counts]
  );

  const handleProviderCategoryChange: SelectProps['onChange'] = e => {
    const category = e.target.value as string;
    manuallyUpdatedRef.current = true;
    setProviderCategory(category);
    setProviderTitle(defaultProviderTitle);
    if (category === defaultProviderCategory) {
      setProviderType('');
      setProviderSpecialty('');
    } else {
      setProviderType(category);
      setProviderSpecialty('');
    }
  };

  const handleProviderTitleChange: SelectProps['onChange'] = e => {
    const title = e.target.value as string;
    manuallyUpdatedRef.current = true;
    setProviderTitle(title);
    if (title === defaultProviderTitle) {
      setProviderSpecialty('');
    } else {
      setProviderSpecialty(title);
    }
  };

  return (
    <div>
      <Header>
        Provider Type <span style={{ color: 'red' }}>*</span>
      </Header>
      <StyledSelect
        labelId="provider-category"
        id="provider-type-search"
        value={providerCategory}
        onChange={handleProviderCategoryChange}
        disableUnderline
        disabled={isLoading}
        displayEmpty
        renderValue={selected => (selected === '' ? 'Select Provider Type' : selected) as string}
        fullWidth
      >
        {categories.map(category => (
          <StyledMenuItem key={category} value={category}>
            <MenuItemText>{category}</MenuItemText>
            <MenuItemSubText>
              {getProviderCountText(counts.categories[category])}
              {' in '}
              {
                Object.keys(providerApiData?.[category]?.Speciality || {}).filter(
                  title => counts.titles[title] > 0
                ).length
              }{' '}
              {Object.keys(providerApiData?.[category]?.Speciality || {}).length === 1
                ? 'Specialty'
                : 'Specialties'}
            </MenuItemSubText>
          </StyledMenuItem>
        ))}
      </StyledSelect>

      {/* Always show Provider Specialty */}
      <>
        <Header2>Provider Specialty</Header2>
        <StyledSelect
          labelId="provider-title"
          id="provider-specialty-search"
          value={providerTitle}
          onChange={handleProviderTitleChange}
          disableUnderline
          disabled={isLoading}
          renderValue={selected => selected as string}
          fullWidth
        >
          <StyledMenuItem value={defaultProviderTitle}>{defaultProviderTitle}</StyledMenuItem>
          {titles?.map(title => (
            <StyledMenuItem key={title} value={title}>
              <MenuItemText>{title}</MenuItemText>
              <MenuItemSubText>{getProviderCountText(counts.titles[title])}</MenuItemSubText>
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </>
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ProviderTypeFilter);
